import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { KeyValuePipe, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrModal } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { FormGroupOfType } from 'src/app/types/shared';
import {
  assertBaseDataTable,
  BaseDataRoute,
  BaseItemsWrapperComponent,
} from '../../_modals/baseitems-wrapper/baseitems-wrapper.component';
import { errorHandle } from 'src/app/misc/functions';

//#region REGEX
const SYSTEM_I = /^strValue(0[2-3|6-9]|1[0-9]|20)$/; //* str: 02, 03, 06-20
const SYSTEM_EXPORT_I = /^strValue(0[2-3|8-9]|10|14)$/; //* str: 02, 03, 08, 09, 10, 14
const SYSTEM_PFLICHT_I = /^strValue(0[2|8-9]|20)$/; //* str: 02, 08, 09, 20

const SYSTEM_II = /^(strValue(0[1-8]|2[1-7])|intValue(01|02))$/; //* str: 01-08, 21-27; int: 01, 02
const SYSTEM_PFLICHT_II = /^strValue(01|02)$/; //* str: 01, 02
//#endregion

@Component({
  selector: 'app-bauform-verwaltung',
  templateUrl: './bauform-verwaltung.component.html',
  imports: [
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    CdkDropList,
    CdkDrag,
    // CdkDragHandle,
    CdkDragPreview,
    MrTranslatePipe,
    TablePrettyPrintPipe,
    TitleCasePipe,
    KeyValuePipe,
    NgTemplateOutlet,
    HideIDColumnsPipe,
    BaseItemsWrapperComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BauformVerwaltungComponent {
  private readonly api = inject(APIService);
  private readonly confirm = inject(ConfirmService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);

  public closedialog = new Subject<boolean>();
  protected readonly BAUFORM_TYPEN: Record<string, BauformTyp> = {
    Text: BauformTyp.strValue,
    Ganzzahl: BauformTyp.intValue,
    Kommazahl: BauformTyp.dblValue,
    'Ja/Nein': BauformTyp.chkValue,
    Datum: BauformTyp.dateValue,
  };
  //#region Selected
  protected readonly selectedKomponente = signal<Komponente>(undefined);
  private readonly OTYPID = computed(() => this.selectedKomponente()?.ID);

  protected readonly openedBauform = linkedSignal<Bauform>(() => (this.bauform(), null));
  private readonly DBField = computed(() => this.openedBauform()?.SFELDNA);

  protected readonly editTabelle = computed<BaseDataRoute>(() => {
    if (!this.openedBauform()?.TABWERT) return null;
    const werteListe: WerteListe = this.werteListe() ?? {
      Tabwert: this.openedBauform().TABWERT,
      Id: this.openedBauform().TABWERT_ID,
      Name: this.openedBauform().Feldname,
    };
    if (!werteListe || this.isNeuWerteListe()) return null;
    const { Tabwert: tabwert, Id: id, Name: name } = werteListe;
    assertBaseDataTable(tabwert);
    return { tabelle: tabwert, sub_id: id, title: name };
  });
  protected readonly isBaseItemModalOpen = signal(false);
  //#endregion

  //#region Resources
  private readonly resortResource = resource({ loader: () => this.api.resortAlleBauformen() });
  //#region Komponenten
  private readonly komponentenResource = resource({
    loader: () => this.api.getAllKomponenten(),
    defaultValue: [],
  });
  protected readonly komponenten = this.komponentenResource.value;
  //#endregion
  //#region Bauform
  private readonly bauformResource = resource({
    params: this.OTYPID,
    loader: ({ params }) => this.api.getBauformByOTYPID(params),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly bauform = this.bauformResource.value;
  protected readonly colLength = computed(() => this.bauform().columns.length);
  //#endregion
  //#region Wertelisten
  private readonly werteListenResource = resource({
    loader: () => this.api.getAllWerteListen(),
    defaultValue: [],
  });
  protected readonly werteListen = this.werteListenResource.value;
  //#endregion
  //#endregion

  //#region Loading
  protected readonly isLoadingTree = computed(
    () => this.komponentenResource.isLoading() || this.resortResource.isLoading()
  );
  protected readonly isLoadingTable = this.bauformResource.isLoading;

  protected readonly getChildren = (node: Komponente) => node.Children;
  //#endregion

  protected readonly bauformForm = new FormGroupOfType<BauformModel>({
    id: new FormControl<number>(null),
    feldtyp: new FormControl<BauformTyp>(BauformTyp.strValue, { nonNullable: true, validators: Validators.required }),
    feldname: new FormControl<string>(null, Validators.required),
    isPflicht: new FormControl(false, { nonNullable: true }),
    useWerteListe: new FormControl(false, { nonNullable: true }),
    werteListe: new FormControl<WerteListe>(null),
    isExport: new FormControl(true, { nonNullable: true }),
  });

  protected readonly bauformTyp = toSignal(this.bauformForm.controls.feldtyp.valueChanges);
  private readonly werteListe = toSignal(this.bauformForm.controls.werteListe.valueChanges);
  private readonly isNeuWerteListe = computed(() => this.werteListe()?.Name == EMPTY_WERTE_LISTE);

  //#region canAction?
  private readonly isStrValue = computed(() => this.bauformTyp() == BauformTyp.strValue);
  private readonly isChkValue = computed(() => this.bauformTyp() == BauformTyp.chkValue);
  // private readonly isIntValue = computed(() => this.openedBauform()?.feldtyp == Feldtyp.intValue);

  protected readonly canUsePflichtFeld = computed(() => !this.isChkValue());
  protected readonly canUseWerteListe = computed(() => this.isStrValue());

  private readonly isSystemStandart = computed(
    () =>
      this.DBField() &&
      ((this.OTYPID() == 1 && SYSTEM_I.test(this.DBField())) || (this.OTYPID() == 2 && SYSTEM_II.test(this.DBField())))
  );
  private readonly isSystemPflicht = computed(
    () =>
      this.isSystemStandart() &&
      ((this.OTYPID() == 1 && SYSTEM_PFLICHT_I.test(this.DBField())) ||
        (this.OTYPID() == 2 && SYSTEM_PFLICHT_II.test(this.DBField())))
  );
  private readonly isSystemExport = computed(
    () =>
      this.isSystemStandart() && ((this.OTYPID() == 1 && SYSTEM_EXPORT_I.test(this.DBField())) || this.OTYPID() == 2)
  );

  protected readonly canDeleteBauform = computed(() => !this.isSystemStandart());
  protected readonly canSaveBauform = computed(
    () =>
      this.canChangeFeldName() || this.canChangePflichtFeld() || this.canChangeExport() || this.canChangeWerteListe()
  );
  protected readonly canChangeFeldName = computed(() => !this.isSystemStandart() || this.OTYPID() == 1);
  protected readonly canChangePflichtFeld = computed(() => !this.isSystemPflicht());
  protected readonly canChangeExport = computed(() => !this.isSystemExport());
  protected readonly canChangeWerteListe = computed(() => !this.isSystemStandart());

  protected readonly canSortIndex = computed(() => (this.OTYPID() == 2 ? 16 : 0));
  protected readonly canSortFn = (index: number) =>
    !this.openedBauform() && index - this.colLength() >= this.canSortIndex();
  //#endregion

  constructor() {
    effect(() => this.bauformForm.reset(this.bauformToModel(this.openedBauform())));

    effect(() => this.activateControlIf('feldname', this.canChangeFeldName()));
    effect(() => this.activateControlIf('isPflicht', this.canUsePflichtFeld() && this.canChangePflichtFeld()));
    effect(() => this.activateControlIf('isExport', this.canChangeExport()));
    effect(() => {
      const enabled = this.canUseWerteListe() && this.canChangeWerteListe();
      this.activateControlIf('useWerteListe', enabled);
      this.activateControlIf('werteListe', enabled);
    });

    //* Werteliste is required, if use Werteliste
    this.bauformForm.controls.useWerteListe.valueChanges.subscribe(use => {
      const wlCtrl = this.bauformForm.controls.werteListe;
      if (use) wlCtrl.setValidators(Validators.required);
      else wlCtrl.clearValidators();

      wlCtrl.updateValueAndValidity();
    });
  }

  private activateControlIf(path: keyof BauformModel, enabled: boolean) {
    if (enabled) this.bauformForm.get(path).enable();
    else this.bauformForm.get(path).disable();
  }

  private bauformToModel(bauform: Bauform): BauformModel {
    if (!bauform) return undefined;

    const { TABWERT, TABWERT_ID: id } = bauform;
    const werteListe = !TABWERT ? undefined : this.werteListen().find(wl => wl.Tabwert === TABWERT && wl.Id === id);

    return {
      id: bauform.ID,
      feldtyp: bauform.Feldtyp,
      feldname: bauform.Feldname,
      isPflicht: bauform.PFLICHT,
      useWerteListe: bauform.USEWERT,
      werteListe: werteListe,
      isExport: bauform.Export,
    };
  }

  protected async deleteBauform() {
    const ok = await this.confirm.danger(
      this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Zeile löschen wollen?')
    );
    if (!ok) return;

    try {
      await this.api.deleteBauform(this.openedBauform().ID);
      this.bauformResource.reload();
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gelöscht'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async saveBauform(modal: ClrModal) {
    try {
      const sendObj = this.bauformForm.value;
      await this.api.saveBauform(this.OTYPID(), sendObj);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.bauformResource.reload();
      if (this.isNeuWerteListe()) this.werteListenResource.reload();
      modal.close();
    } catch (er) {
      const error = errorHandle(er);
      if (error == 'Max.Typ') this.bauformForm.controls.feldtyp.setErrors({ max: true });
      else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async moveDrop(event: CdkDragDrop<Bauform[], Bauform[], number>) {
    if (event.previousContainer != event.container) return;
    const change = event.currentIndex - event.previousIndex;
    if (change == 0) return;

    try {
      const rows = [...this.bauform().rows];
      const oldIndex = event.item.data;
      const newIndex = oldIndex + change;

      moveItemInArray(rows, oldIndex, newIndex);

      await this.api.resortBauform(rows.map(r => r.ID));
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.bauformResource.reload();
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected close() {
    this.closedialog.next(true);
  }
}

export interface Komponente {
  ID: number;
  Icon: number;
  Bezeichnung: string;
  expanded: boolean;
  Children: Komponente[];
}

export interface WerteListe {
  Id: number | null;
  Name: string;
  Tabwert: string;
  Icon?: number;
}

const EMPTY_WERTE_LISTE = '**NEU**';

export interface Bauform {
  ID: number;
  SFELDNA: string;
  Feldname: string;
  Feldtyp: BauformTyp;
  PFLICHT: boolean;
  USEWERT: boolean;
  TABWERT: string | null;
  TABWERT_ID: number | null;
  KSORT: number;
  Export: boolean;
}

export interface BauformModel {
  id: number;
  feldtyp: BauformTyp;
  feldname: string;
  isPflicht: boolean;
  useWerteListe: boolean;
  werteListe: WerteListe;
  isExport: boolean;
}

const enum BauformTyp {
  strValue = 1,
  intValue,
  dblValue,
  chkValue,
  dateValue,
}

// private readonly isStrValue = computed(() => /^str/.test(this.DBField()));
// private readonly isChkValue = computed(() => /^chk/.test(this.DBField()));
// private readonly isIntValue = computed(() => /^int/.test(this.DBField()));
